import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  memo,
  useMemo
} from 'react'
import { Maybe, Project } from '@store/graphql/__generated__/schema'
import ProjectCard from '@components/project/ProjectCard'
import { ProjectCardSizeLimits } from './ProjectCardSize'
import styles from '@styles/components/ProjectList.module.scss'
import { ProjectListType } from './ProjectList'
import { useFormatter } from 'use-intl'

interface ProjectCardTimelineProps {
  isAdmin: boolean
  projectListType: ProjectListType
  projects: Array<Maybe<Project>>
  nextOffset: number | null | undefined
  showMoreProjects?: () => void
}

type ProjectUUID = string
type FormattedProjectDateMap = Record<ProjectUUID, FormattedProjectDateStrings>
type FormattedProjectDateStrings = {
  forTimeline: string
  forProjectCard: string
}

const DATE_PLACEHOLDER = '•'
const CARD_WIDTH = ProjectCardSizeLimits.max.width
const GAP_WIDTH = 10
const PLACEHOLDER_SEGMENT_COUNT = 6
const EQUAL_SPLIT_CARD_AND_GAP_WIDTH =
  (CARD_WIDTH + GAP_WIDTH) / PLACEHOLDER_SEGMENT_COUNT

const ProjectCardTimeline: React.FC<ProjectCardTimelineProps> = ({
  isAdmin,
  projectListType,
  projects,
  nextOffset,
  showMoreProjects
}) => {
  const projectListRef = useRef<HTMLDivElement>(null)
  const timelineMeterRef = useRef<HTMLDivElement>(null)
  const leftBoundaryInfiniteLoader = useRef<HTMLDivElement>(null)
  const [stickyDate, setStickyDate] = useState<string | null>(null)

  const formatter = useFormatter()
  const [formattedProjectDates, setFormattedProjectDates] =
    useState<FormattedProjectDateMap>({})

  const createFormattedProjectDateStrings = (
    projectUuid: Project['uuid'],
    projectCreatedTimestamp: Project['insertedAt']
  ): FormattedProjectDateStrings => {
    const now = Date.now()
    const date = new Date(projectCreatedTimestamp as string)
    const relativeDatesForProject = {
      forTimeline: formatter
        .relativeTime(date, {
          now,
          style: 'narrow'
        })
        .replace('ago', '')
        .trim(),
      forProjectCard: formatter.relativeTime(date, { now })
    }

    setFormattedProjectDates(prev => ({
      ...prev,
      [projectUuid as ProjectUUID]: relativeDatesForProject
    }))

    return relativeDatesForProject
  }

  // Populate all the first occurrences of unique dates
  // Replace subsequent duplicates with placeholders
  const timelineMeterElements = useMemo(() => {
    const uniqueDates: Set<string> = new Set()
    return projects.map(project => {
      const formattedDate = createFormattedProjectDateStrings(
        project!.uuid,
        project!.insertedAt
      ).forTimeline
      if (uniqueDates.has(formattedDate)) {
        return DATE_PLACEHOLDER
      } else {
        uniqueDates.add(formattedDate)
        return formattedDate
      }
    })
  }, [projects])

  // When the project list scrolls, update the sticky date
  // Replace the rightmost instance of a placeholder with an actual relevant date
  const setStickyDateOnScroll = useCallback(
    e => {
      const projectListScrollLeft = e.target.scrollLeft
      if (timelineMeterRef.current) {
        // Make sure to update the timeline meter scrollLeft while we're at it
        // Necessary for when new projects are inserted into the list via subscription
        timelineMeterRef.current.scrollLeft = projectListScrollLeft
      }

      const startIndex = Math.abs(
        Math.ceil(projectListScrollLeft / (CARD_WIDTH + GAP_WIDTH))
      )

      const firstProjectInView = projects[startIndex]
      const firstDisplayDateInView = timelineMeterElements[startIndex]

      if (firstDisplayDateInView === DATE_PLACEHOLDER) {
        const newStickyDate = createFormattedProjectDateStrings(
          firstProjectInView!.uuid,
          firstProjectInView!.insertedAt
        ).forTimeline

        setStickyDate(newStickyDate)
      } else {
        setStickyDate(null)
      }
    },
    [projects, timelineMeterElements]
  )

  // Wheel up --> scroll right
  // Wheel down <-- scroll left
  const handleWheel = (e: WheelEvent) => {
    if (timelineMeterRef.current && projectListRef.current) {
      timelineMeterRef.current.scrollLeft -= e.deltaY
      timelineMeterRef.current.scrollLeft += e.deltaX
      projectListRef.current.scrollLeft -= e.deltaY
      projectListRef.current.scrollLeft += e.deltaX
    }
  }

  useEffect(() => {
    window.addEventListener('wheel', handleWheel, { passive: true })
    return () => {
      window.removeEventListener('wheel', handleWheel)
    }
  }, [handleWheel])

  const preventManualScroll = e => e.preventDefault()

  // Disable user manually scrolling on the timeline and project list
  // We want to use the window event listener first and foremost
  // This is a design requirement; the user should be able to scroll from anywhere on the page
  useEffect(() => {
    if (timelineMeterRef.current) {
      timelineMeterRef.current.addEventListener('wheel', preventManualScroll, {
        passive: false
      })
    }

    return () => {
      if (timelineMeterRef.current) {
        timelineMeterRef.current.removeEventListener(
          'wheel',
          preventManualScroll
        )
      }
    }
  }, [timelineMeterRef])

  useEffect(() => {
    if (projectListRef.current) {
      projectListRef.current.addEventListener('wheel', preventManualScroll, {
        passive: false
      })
    }

    return () => {
      if (projectListRef.current) {
        projectListRef.current.removeEventListener('wheel', preventManualScroll)
      }
    }
  }, [projectListRef])

  // Infinite scroll <-- left
  useEffect(() => {
    let observer: IntersectionObserver
    if (leftBoundaryInfiniteLoader.current && nextOffset) {
      observer = new IntersectionObserver(
        ([firstEntry]) => {
          if (firstEntry.isIntersecting) showMoreProjects?.()
        },
        {
          root: projectListRef.current,
          threshold: 1.0
        }
      )
      observer.observe(leftBoundaryInfiniteLoader.current)
    }
    return () => observer?.disconnect()
  }, [leftBoundaryInfiniteLoader.current, nextOffset])

  return (
    <div className={styles['timeline-root']}>
      <div className={styles['timeline-sticky-date']}>{stickyDate}</div>
      <div
        ref={timelineMeterRef}
        style={{ height: 10 }}
        className={styles['timeline-horizontal-scroll']}>
        {timelineMeterElements.map((date, index) => (
          <div
            key={index}
            className={styles['timeline-entry-root']}
            style={{ minWidth: CARD_WIDTH }}>
            <div className={styles['timeline-entry-container']}>
              {Array.from({ length: PLACEHOLDER_SEGMENT_COUNT }).map(
                (_e, i) => (
                  <div
                    key={i}
                    className={styles['timeline-entry-placeholder']}
                    style={{
                      visibility: index === 0 ? 'hidden' : 'visible',
                      position: 'absolute',
                      top: 0,
                      left: EQUAL_SPLIT_CARD_AND_GAP_WIDTH * i
                    }}>
                    •
                  </div>
                )
              )}
              <div className={styles['timeline-entry-date']}>
                {date === DATE_PLACEHOLDER ? (
                  <div className={styles['timeline-entry-placeholder']}>
                    {DATE_PLACEHOLDER}
                  </div>
                ) : (
                  date
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div
        ref={projectListRef}
        onScroll={setStickyDateOnScroll}
        className={styles['timeline-horizontal-scroll']}>
        {projects.map(project => (
          <ProjectCard
            key={`project-card-${project?.uuid}`}
            uuid={project?.uuid!}
            name={project?.name!}
            imageUrl={project?.imageUrl!}
            documentFrameSize={project?.documentFrameSize}
            getCachedCardHeight={undefined}
            createdAt={project?.insertedAt!}
            projectOwnerUuid={project?.ownerUserUuid}
            public={project?.public}
            featured={project?.featured}
            totalReactions={project?.totalReactions}
            userReactedWith={project?.userReactedWith}
            uniqueReactionTypes={project?.uniqueReactionTypes || []}
            adminEditingEnabled={isAdmin}
            showShareBtn={true}
            showReactions={true}
            editingEnabled={projectListType === ProjectListType.CurrentUser}
            precomputedDate={
              formattedProjectDates[project!.uuid!]?.forProjectCard ?? undefined
            }
          />
        ))}
        <div
          ref={leftBoundaryInfiniteLoader}
          style={{ flex: '0 0 auto', width: '1px', height: '1px' }}></div>
      </div>
    </div>
  )
}

export default memo(
  ProjectCardTimeline,
  (prevProps, nextProps) =>
    prevProps.isAdmin === nextProps.isAdmin &&
    prevProps.projectListType === nextProps.projectListType &&
    prevProps.projects === nextProps.projects &&
    prevProps.nextOffset === nextProps.nextOffset &&
    prevProps.showMoreProjects === nextProps.showMoreProjects
)
